import type { AssetPack } from '~/../types/AssetPack';
import { App, app } from '../App';

export interface SceneAssets {
  hasDynamicAssets?: boolean;
  requiredAssets: string[];
}

export class AssetManager {
  protected areFontsLoaded: boolean;
  protected assetLookup: Map<string, any> | undefined;
  protected baseAssets: Set<string> | undefined;
  protected sceneIdToAssetDependencies: Map<string, SceneAssets> | undefined;
  protected loadedAssets: Set<string>;
  protected dynamicAssets: Set<string>;

  constructor() {
    this.areFontsLoaded = false;

    this.loadedAssets = new Set();
    this.dynamicAssets = new Set();
  }

  get AreFontsLoaded() {
    return this.areFontsLoaded;
  }

  public fontsReady() {
    this.areFontsLoaded = true;
    app.emit(App.events.FontsLoaded);
  }

  public setAssetMaps(assetLookup: Map<string, any>, baseAssets: Set<string>, sceneIdToAssets: Map<string, SceneAssets>) {
    this.assetLookup = assetLookup;
    this.baseAssets = baseAssets;
    this.sceneIdToAssetDependencies = sceneIdToAssets;
  }

  public addDynamicAssets(assetPacks: string[]) {
    for (const assetPackId of assetPacks) {
      this.addDynamicAsset(assetPackId);
    }
  }

  public addDynamicAsset(assetPackId: string) {
    this.dynamicAssets.add(assetPackId);
  }

  public resetPostLoad() {
    this.dynamicAssets.clear();
  }

  public loadAssetsForScene(scene: Phaser.Scene) {
    let sceneAssets = this.sceneIdToAssetDependencies?.get(scene.scene.key);
    if (!sceneAssets || this.assetLookup == null || this.baseAssets == null) return;

    // TODO: Merge base assets and required assets.

    for (const assetPackId of sceneAssets.requiredAssets) {
      let asset = this.assetLookup.get(assetPackId);
      if (asset) {
        scene.load.pack(assetPackId, asset);
        this.loadedAssets.add(assetPackId);
        console.log(
          '%c%s%c%s',
          'background: #2192FF; color: #fff; padding: 2px 5px; border-radius: 5px; margin-right: 8px;',
          'Loading Asset',
          'color: #0000ff; padding: 2px;',
          `${assetPackId}`
        );
      }
    }

    if (sceneAssets.hasDynamicAssets) {
      for (const assetPack of this.dynamicAssets) {
        let assetPackId = this.assetLookup.get(assetPack);
        if (assetPackId) {
          scene.load.pack(assetPack, assetPackId);
          this.loadedAssets.add(assetPack);
          console.log(
            '%c%s%c%s',
            'background: #2192FF; color: #fff; padding: 2px 5px; border-radius: 5px; margin-right: 8px;',
            'Loading Dynamic Asset',
            'color: #0000ff; padding: 2px;',
            `${assetPackId}`
          );
        }
      }
    }
  }

  public unloadAssets(scene: Phaser.Scene) {
    let sceneAssets = this.sceneIdToAssetDependencies?.get(scene.scene.key);
    if (!sceneAssets || this.assetLookup == null || this.baseAssets == null) return;

    let assetsToCheck = new Set(this.loadedAssets);

    console.log(
      '%c%s%c%s',
      'background: #2192FF; color: #fff; padding: 2px 5px; border-radius: 5px; margin-right: 8px;',
      'Base Assets Loaded',
      'color: #0000ff; padding: 2px;',
      `${new Array(...this.baseAssets).join(', ')}`
    );

    console.log(
      '%c%s%c%s',
      'background: #2192FF; color: #fff; padding: 2px 5px; border-radius: 5px; margin-right: 8px;',
      'Assets Loaded',
      'color: #0000ff; padding: 2px;',
      `${new Array(...assetsToCheck).join(', ')}`
    );

    for (const assetPackId of assetsToCheck) {
      if (!sceneAssets.requiredAssets.includes(assetPackId) && !this.dynamicAssets.has(assetPackId) && !this.baseAssets.has(assetPackId)) {
        this.unloadPack(scene, assetPackId);
        this.loadedAssets.delete(assetPackId);
        console.log(
          '%c%s%c%s',
          'background: #2192FF; color: #fff; padding: 2px 5px; border-radius: 5px; margin-right: 8px;',
          'Unloading Asset',
          'color: #0000ff; padding: 2px;',
          `${assetPackId}`
        );
      }
    }
  }

  protected unloadPack(scene: Phaser.Scene, packId: string) {
    let assetPack = scene.cache.json.get(packId);
    if (!assetPack) return;

    for (const key of Object.keys(assetPack)) {
      if (key == 'meta') continue;

      if (Array.isArray(assetPack[key]?.files)) {
        for (const file of assetPack[key].files) {
          if (file.type == 'multiatlas') {
            scene.textures.remove(file.key);

            // let animations = animData.filter((val) => {
            //   console.log(val);
            //   return val.key.indexOf(file.key + '-') == 0;
            // });

            // for (let i = 0; i < animations.length; i++) {
            //   scene.anims.remove(animations[i].key);
            // }
          }
        }
      }
    }

    scene.cache.json.remove(packId);
  }
}
